import type { ComputedSlot } from '@vagusx/clients';
import type { SlotGroup } from '../types';

/**
 * Group an array of computed slots by date.
 * Returns groups sorted by date, each with a human-readable day label.
 */
export function groupSlotsByDate(slots: ComputedSlot[]): SlotGroup[] {
  const map = new Map<string, ComputedSlot[]>();

  for (const slot of slots) {
    // Extract date from ISO string using the location timezone
    const date = extractDate(slot.startTime, slot.locationTimezone);
    const existing = map.get(date);
    if (existing) {
      existing.push(slot);
    } else {
      map.set(date, [slot]);
    }
  }

  // Sort by date and build result
  const sortedDates = [...map.keys()].sort();
  return sortedDates.map((date) => ({
    date,
    dayLabel: formatDayLabel(date),
    slots: map.get(date)!,
  }));
}

/**
 * Extract YYYY-MM-DD from an ISO datetime string in a given timezone.
 */
export function extractDate(isoString: string, timezone: string): string {
  const d = new Date(isoString);
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return formatter.format(d); // en-CA gives YYYY-MM-DD format
}

/**
 * Format a date string as a short day label matching widget format.
 * "2025-03-20" → "Fri, Mar 20"
 */
function formatDayLabel(date: string): string {
  const [year, month, day] = date.split('-').map(Number);
  const d = new Date(year!, month! - 1, day!);
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Extract just the time portion from a displayTime string.
 * "Thursday, March 20, 9:00 AM" → "9:00 AM"
 */
export function extractTime(displayTime: string): string {
  const parts = displayTime.split(', ');
  return parts[parts.length - 1] ?? displayTime;
}
